import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useTheme } from '../hooks/useTheme';
import orderService from '../services/orderService';

// Página de confirmación de orden
// Renderiza el módulo de opt‑in de Google Customer Reviews (GCR) con datos de la compra
const OrderConfirmationPage = () => {
    const { theme, toggleTheme } = useTheme();
    const { id: orderIdParam } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const renderOnce = useRef(false);
    const [order, setOrder] = useState(location.state?.order || null);

    // Configuración por variables de entorno
    const merchantId = useMemo(() => parseInt(process.env.REACT_APP_GCR_MERCHANT_ID || '0', 10), []);
    const optInStyle = useMemo(() => (process.env.REACT_APP_GCR_OPT_IN_STYLE || 'CENTER_DIALOG'), []);
    const defaultDeliveryDays = useMemo(() => parseInt(process.env.REACT_APP_GCR_DEFAULT_DELIVERY_DAYS || '5', 10), []);

    // Cargar orden si no viene en el estado de navegación
    useEffect(() => {
        let cancelled = false;
        const loadOrder = async () => {
            try {
                if (!order && orderIdParam) {
                    const resp = await orderService.getOrderById(orderIdParam);
                    if (!cancelled) setOrder(resp?.data || resp || null);
                }
            } catch (e) {
                // Si no se puede cargar, redirigir al perfil
                navigate('/profile');
            }
        };
        loadOrder();
        return () => { cancelled = true; };
    }, [order, orderIdParam, navigate]);

    // Calcular fecha estimada de entrega (YYYY-MM-DD)
    const estimatedDeliveryDate = useMemo(() => {
        try {
            const base = new Date();
            base.setDate(base.getDate() + defaultDeliveryDays);
            const yyyy = base.getFullYear();
            const mm = String(base.getMonth() + 1).padStart(2, '0');
            const dd = String(base.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        } catch {
            return '1970-01-01';
        }
    }, [defaultDeliveryDays]);

    // Renderizar módulo de opt‑in GCR cuando la orden esté disponible
    useEffect(() => {
        if (renderOnce.current) return;
        if (!order) return;
        if (!merchantId) return;

        // Datos mínimos requeridos por GCR
        const orderId = order?.id || orderIdParam || `ORD-${Date.now()}`;
        const email = order?.shipping?.email || order?.billing?.email || order?.customer_email || '';
        const deliveryCountry = (order?.shipping?.country_code || order?.shipping?.country || 'PE').toString().toUpperCase();

        // Cargar el script de plataforma de Google sólo una vez
        const ensurePlatformScript = () => {
            return new Promise((resolve) => {
                if (window.__gcrPlatformLoaded) return resolve(true);
                const existing = document.querySelector('script[src*="apis.google.com/js/platform.js"]');
                if (existing) {
                    window.__gcrPlatformLoaded = true;
                    return resolve(true);
                }
                const s = document.createElement('script');
                s.src = 'https://apis.google.com/js/platform.js?onload=renderOptIn';
                s.async = true;
                s.defer = true;
                document.body.appendChild(s);
                // Marcamos como cargado cuando el script dispare onload
                window.renderOptIn = () => resolve(true);
            });
        };

        const renderOptIn = () => {
            try {
                if (!window.gapi) return;
                window.gapi.load('surveyoptin', function () {
                    try {
                        window.gapi.surveyoptin.render({
                            merchant_id: merchantId,
                            order_id: orderId,
                            email: email,
                            delivery_country: deliveryCountry,
                            estimated_delivery_date: estimatedDeliveryDate,
                            opt_in_style: optInStyle
                        });
                        renderOnce.current = true;
                    } catch (e) {
                        // Evitar romper la UX si la renderización falla
                    }
                });
            } catch (e) {
                // Evitar errores no controlados
            }
        };

        ensurePlatformScript().then(renderOptIn);
    }, [order, merchantId, orderIdParam, optInStyle, estimatedDeliveryDate]);

    return (
        <div className={`landing-page theme-${theme}`}>
            <Helmet>
                <title>Orden confirmada</title>
                <meta name="robots" content="noindex, nofollow" />
                <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL}/orden-confirmada/${orderIdParam || order?.id || ''}`} />
            </Helmet>
            <Header theme={theme} onToggleTheme={toggleTheme} />
            <main className="landing-main">
                <div className="surface-section px-4 py-6 md:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold mb-3">¡Gracias por tu compra!</h1>
                    <p className="text-700 mb-4">Tu orden ha sido registrada correctamente. Pronto recibirás un correo con los detalles.</p>
                    <div className="surface-card border-round p-4">
                        <p className="text-700">Número de orden: <strong>{order?.id || orderIdParam}</strong></p>
                        <p className="text-700">Te invitaremos a dejar una reseña en Google para mejorar la confianza de nuestros clientes.</p>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default OrderConfirmationPage;

